/* Ayurveda Grundlagen – Sacred Modern Design
 * Light section explaining Ayurveda basics
 * 5 pillars of AYU health approach
 * Nature image on one side, text on other
 */
import { PILLARS, IMAGES } from "@/lib/constants";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Leaf, Activity, Brain, Clock, Shield } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const pillarIcons = { leaf: Leaf, activity: Activity, brain: Brain, clock: Clock, shield: Shield };

export default function AyurvedaSection() {
  const { t } = useI18n();
  const { ref, isVisible } = useScrollAnimation();
  const { ref: ref2, isVisible: isVisible2 } = useScrollAnimation();

  return (
    <section id="ayurveda" className="section-ivory py-24 lg:py-32 relative" ref={ref}>
      <div className="gold-line w-full absolute top-0" />

      <div className="container">
        {/* Top: Ayurveda Intro with Image */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24">
          {/* Image */}
          <div
            className={`relative transition-all duration-700 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src={IMAGES.nature}
                alt="Ayurvedische Natur"
                className="w-full h-auto object-cover aspect-[4/3]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[oklch(0.12_0.02_280/0.4)] to-transparent" />
            </div>
            {/* Floating quote */}
            <div className="absolute -bottom-6 -right-4 lg:-right-8 bg-white rounded-xl p-5 shadow-lg border border-[oklch(0.88_0.02_80)] max-w-xs">
              <p className="text-sm italic text-[oklch(0.4_0.04_50)]" style={{ fontFamily: "var(--font-display)" }}>
                {t("landing.ayurveda.quote")}
              </p>
            </div>
          </div>

          {/* Text */}
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
              {t("landing.ayurveda.badge")}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.25_0.04_50)] mt-4 mb-6">
              {t("landing.ayurveda.heading")}
            </h2>

            <div className="space-y-4 text-[oklch(0.45_0.04_50)] leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
              <p>{t("landing.ayurveda.p1")}</p>
              <p>
                {t("landing.ayurveda.p2Text")} <strong className="text-[oklch(0.3_0.04_50)]">„{t("landing.ayurveda.p2Western")}"</strong>
                {t("landing.ayurveda.p2Text2")} <strong className="text-[oklch(0.52_0.09_128)]">„{t("landing.ayurveda.p2Ayurveda")}"</strong>
              </p>
              <p>{t("landing.ayurveda.p3")}</p>
            </div>

            {/* Two approaches comparison */}
            <div className="grid grid-cols-2 gap-4 mt-8">
              <div className="bg-[oklch(0.95_0.01_80)] rounded-xl p-5 border border-[oklch(0.88_0.02_80)]">
                <h4 className="font-bold text-[oklch(0.4_0.04_50)] mb-2 text-sm">{t("landing.ayurveda.pathogenesisTitle")}</h4>
                <p className="text-xs text-[oklch(0.55_0.03_50)]">{t("landing.ayurveda.pathogenesisDesc")}</p>
              </div>
              <div className="bg-[oklch(0.52_0.09_128/0.1)] rounded-xl p-5 border border-[oklch(0.52_0.09_128/0.3)]">
                <h4 className="font-bold text-[oklch(0.52_0.09_128)] mb-2 text-sm">{t("landing.ayurveda.ayurvedaTitle")}</h4>
                <p className="text-xs text-[oklch(0.5_0.03_50)]">{t("landing.ayurveda.ayurvedaDesc")}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom: 5 Pillars */}
        <div ref={ref2}>
          <div
            className={`text-center mb-12 transition-all duration-700 ${
              isVisible2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <h3 className="text-2xl sm:text-3xl font-bold text-[oklch(0.25_0.04_50)]">
              {t("landing.ayurveda.pillarsHeading")}
            </h3>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {PILLARS.map((pillar, i) => {
              const Icon = pillarIcons[pillar.icon as keyof typeof pillarIcons];
              return (
                <div
                  key={i}
                  className={`bg-white rounded-2xl p-6 text-center shadow-sm border border-[oklch(0.88_0.02_80)] hover:shadow-md hover:border-[oklch(0.52_0.09_128/0.3)] transition-all duration-300 ${
                    isVisible2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                  }`}
                  style={{ transitionDelay: `${i * 100 + 200}ms`, transitionDuration: "700ms" }}
                >
                  <div className="w-12 h-12 rounded-full bg-[oklch(0.52_0.09_128/0.1)] flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-[oklch(0.52_0.09_128)]" size={22} />
                  </div>
                  <h4 className="font-bold text-[oklch(0.25_0.04_50)] mb-2">{t(pillar.titleKey as any)}</h4>
                  <p className="text-sm text-[oklch(0.5_0.03_50)]" style={{ fontFamily: "var(--font-body)" }}>
                    {t(pillar.descriptionKey as any)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
